import React from 'react'
import { EndPointDoc } from './apireftypes';
import { Link } from 'react-router-dom';

interface Props {
    endpoints: EndPointDoc[];
}

export class ApiOverview extends React.Component<Props> {
    render() {
        return (
            <>
                <h1>API Reference</h1>
                <p>Welcome to the TigerCity.ai API Reference. Below is an overview of all available endpoints.</p>

                <h2>How API Endpoints Work</h2>
                <p>
                    API endpoints are URLs that your application can call to interact with TigerCity.ai services. 
                    Each endpoint performs a specific action, such as creating a model, getting a response, or managing your account. 
                    You make HTTP requests (GET, POST, DELETE, etc.) to these endpoints with your API token for authentication.
                </p>

                <h2>Obtaining an API Token</h2>
                <p>
                    To use the TigerCity.ai API, you need an API token for authentication. Here's how to get one:
                </p>
                <ol>
                    <li>Go to the <strong>Playground</strong> section</li>
                    <li>Navigate to <strong>ApiKeys</strong></li>
                    <li>Click the <strong>(+)</strong> button to create a new API key</li>
                    <li>Give it a name and you will get a dialog showing the new key</li>
                    <li>Copy the key and set it in your environment</li>
                </ol>
                <p>
                    <strong>Important:</strong> When the key is generated, it is only shown once and will not be displayed again.
                    Make sure to save it immediately, as you won't be able to retrieve it later. If you lose the key, you'll need to create a new one.
                </p>
                <p>Example:</p>
                <pre><code>export TIGER_API_KEY=&lt;your app key&gt;</code></pre>

                <h3>Setting the API Key Permanently</h3>

                <h4>Linux / macOS</h4>
                <p>
                    Add the export command to your shell configuration file:
                </p>
                <ul>
                    <li>For <strong>bash</strong>: Add to <code>~/.bashrc</code></li>
                    <li>For <strong>zsh</strong>: Add to <code>~/.zshrc</code></li>
                </ul>
                <p>Open the file in a text editor and add:</p>
                <pre><code>export TIGER_API_KEY=&lt;your_api_key_here&gt;</code></pre>
                <p>
                    Then reload your shell configuration: <code>source ~/.bashrc</code> (or <code>source ~/.zshrc</code> for zsh)
                </p>

                <h4>Windows</h4>
                <p>
                    For <strong>Command Prompt</strong>:
                </p>
                <ul>
                    <li>Open System Properties → Advanced → Environment Variables</li>
                    <li>Under "User variables", click "New"</li>
                    <li>Variable name: <code>TIGER_API_KEY</code></li>
                    <li>Variable value: your API key</li>
                    <li>Click OK and restart your terminal</li>
                </ul>
                <p>
                    For <strong>PowerShell</strong>:
                </p>
                <ul>
                    <li><p>Run:</p>
                        <pre><code>[System.Environment]::SetEnvironmentVariable('TIGER_API_KEY', 'your_api_key_here', 'User')</code></pre>
                    </li>
                    <li>Restart PowerShell for the changes to take effect</li>
                </ul>

                <h2>Streaming</h2>
                <p>
                    Many endpoints support streaming responses for real-time feedback. Learn more about how streaming works 
                    and how to implement it in your application: <Link to="/apiref/streaming">Streaming Guide</Link>
                </p>

                <h2>Available Endpoints</h2>
                <div className="endpoints-overview">
                    {this.props.endpoints.map((endpoint, index) => (
                        <div key={index} className="endpoint-card">
                            <div className="endpoint-header">
                                <h3>
                                    <Link to={`/apiref/${endpoint.docurl}`}>
                                        {endpoint.title}
                                    </Link>
                                </h3>
                            </div>
                            <div className="endpoint-url">{endpoint.url}</div>
                            <div className="endpoint-description">
                                {endpoint.description}
                            </div>
                        </div>
                    ))}
                </div>
            </>
        );
    }
}

